import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, setToken } from '../api/client';
import { setPixelSuppressed } from '../lib/pixel';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('iamhero_token');
    if (!token) { setLoading(false); return; }
    api.get('/api/auth/me')
      .then((data) => setUser(data.user))
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  // Mute the browser Meta pixel for staff (admins + editors) so the team's own
  // browsing and free book creation never reach Meta (server CAPI is already
  // gated via order.isTest). Runs on every user change — login, logout, and the
  // cold-load /me hydration above — keeping the pixel suppression flag in sync.
  useEffect(() => {
    setPixelSuppressed(user?.role === 'admin' || user?.role === 'editor');
  }, [user]);

  const login = useCallback(async (email, password) => {
    const data = await api.post('/api/auth/login', { email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, nameAr) => {
    const data = await api.post('/api/auth/register', { email, password, nameAr });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  // Used by /checkout-success (one-shot autoLoginToken in the status response)
  // and /login?autoToken=… (magic-link redirect). Sets the token, then hydrates
  // the user from /me so role/email/etc are populated.
  const applyToken = useCallback(async (token) => {
    setToken(token);
    const data = await api.get('/api/auth/me');
    setUser(data.user);
    return data.user;
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, applyToken,
      // isAdmin = full admin only (money, settings, stats, templates, users).
      // isStaff = admin OR editor — the shared content powers (previews,
      // stories, order fulfillment, viewing orders).
      isAdmin: user?.role === 'admin',
      isStaff: user?.role === 'admin' || user?.role === 'editor',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
